// ─────────────────────────────────────────────────────────────────────────────────────────────
// centroCosto.js — EL CENTRO DE COSTO 225 «05-1» DE SOLAR HUB, leído de root (26-oct-2026).
//
// 🔑 *«cada app debe manejar su centro de costos en su respectivo admin»* (dueño, 25-oct-2026).
// El hub-api (`server/routes/centro-costo.js`) le pregunta a root con la llave de la línea
// `solarhub`, que solo puede leer el 225. Esta pantalla NO suma por su lado: toma los totales de
// root y solo los ordena para leerlos. Las funciones puras de abajo las prueba
// `__tests__/centro-costo.test.js`. Es el mismo modelo que `web/src/lib/centro-costo.ts` de FluxAI.
//
// 🔴 La utilidad es AL CORTE del último refresco y se dice así. Si root la niega (fuentes de horas
//    distintas o una que falta), queda en `null` con el motivo de root, nunca en 0.
// 🪤 Venta y recaudo NO se suman: una FV de $100 cobrada con un RC de $100 son $100 de ingreso.

import { hubGet, hubGetBlob } from './hub';
import { tokenParaBackend } from './adminAuth';

/** Cabecera de los GET gateados por `exigeAdmin` (igual que `services/empresa.js`). */
function auth() {
  const t = tokenParaBackend();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}

/** El centro tal como lo da root (JSON crudo). Lo lee `normalizarCentro`. */
export async function leerCentroCosto({ desde, hasta } = {}) {
  return hubGet('empresa/centro-costo', { desde, hasta }, { timeoutMs: 30000, ...auth() });
}

/** El PDF de una factura del centro, para el visor del admin (nunca un enlace externo). */
export async function pdfDeFactura(id) {
  return hubGetBlob(`empresa/centro-costo/factura/${encodeURIComponent(id)}/pdf`, { timeoutMs: 30000, ...auth() });
}

// ───────────────────────────────────── puras ─────────────────────────────────────

const num = (v) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : 0);
const numONull = (v) => (v === null || v === undefined || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null);
const txt = (v) => (v === null || v === undefined || String(v).trim() === '' ? null : String(v));
const obj = (v) => (v && typeof v === 'object' ? v : {});

/** La respuesta de root → lo que la pantalla lee. `null` si no es una respuesta válida. PURA. */
export function normalizarCentro(j) {
  const r = obj(j);
  if (r.ok !== true) return null;
  const centro = obj(r.centro);
  const tot = obj(r.totales);
  const dev = obj(tot.devengado);
  const caja = obj(tot.caja);
  const corte = obj(r.al_corte);
  const filtro = obj(r.filtro);
  const fuentes = Array.isArray(r.fuentes) ? r.fuentes.map(obj) : [];
  const minutos = fuentes.map((f) => numONull(f.minutos)).filter((m) => m !== null);
  const movimientos = (Array.isArray(r.movimientos) ? r.movimientos : []).map(obj).map((m) => ({
    id: txt(m.id),
    naturaleza: String(m.naturaleza ?? ''),
    documento: txt(m.documento),
    fecha: txt(m.fecha),
    concepto: txt(m.concepto),
    valorCentavos: num(m.valor_centavos),
    saldoCentavos: numONull(m.saldo_centavos),
    cuenta: txt(m.cuenta),
    movimiento: txt(m.movimiento),
  }));
  const neto = numONull(corte.neto_centavos);
  return {
    centroId: numONull(centro.id),
    centroNombre: txt(centro.centro) ?? 'Solar Hub',
    desde: txt(filtro.desde),
    hasta: txt(filtro.hasta),
    ventasCentavos: num(dev.ventas_centavos),
    ventasBaseCentavos: numONull(dev.ventas_base_centavos),
    comprasCentavos: num(dev.compras_centavos),
    comprasBaseCentavos: numONull(dev.compras_base_centavos),
    ajusteGastoCcCentavos: numONull(dev.ajuste_gasto_cc_centavos),
    ajusteIngresoCcCentavos: numONull(dev.ajuste_ingreso_cc_centavos),
    comprobantesEnAjuste: num(dev.comprobantes_en_ajuste),
    recaudosCentavos: num(caja.recaudos_centavos),
    cuentasCobroCentavos: num(dev.cuentas_cobro_centavos),
    viaticosCentavos: num(dev.viaticos_centavos),
    porCobrarCentavos: num(tot.por_cobrar_centavos),
    porPagarCentavos: num(tot.por_pagar_centavos),
    netoCentavos: neto,
    corte: txt(corte.corte),
    motivoSinNeto: neto === null ? (txt(corte.motivo) ?? 'sin utilidad: root no la entregó') : null,
    minutosMasViejo: minutos.length ? Math.max(...minutos) : null,
    advertencias: Array.isArray(r.advertencias) ? r.advertencias.map(String) : [],
    // `false` = alguna fuente del libro no llegó entera: las cifras pueden quedarse cortas, y se dice.
    completo: r.completo !== false,
    movimientos,
    truncado: num(obj(r.conteo).total) > movimientos.length,
  };
}

export const ETIQUETA = {
  venta: 'Facturas de venta',
  recaudo: 'Pagos recibidos',
  compra: 'Compras',
  asiento: 'Comprobantes',
  cuenta_cobro: 'Cuentas de cobro',
  viatico: 'Viáticos',
};

/** Los movimientos de una naturaleza, del más reciente al más viejo. PURA. */
export function deNaturaleza(c, n) {
  return c.movimientos
    .filter((m) => m.naturaleza === n)
    .sort((a, b) => (b.fecha ?? '').localeCompare(a.fecha ?? '') || (a.documento ?? '').localeCompare(b.documento ?? ''));
}

/** Cuántos hay de cada naturaleza, en el orden en que se leen (ventas primero). PURA. */
export function conteoPorNaturaleza(c) {
  return Object.keys(ETIQUETA)
    .map((n) => ({ naturaleza: n, etiqueta: ETIQUETA[n], n: c.movimientos.filter((m) => m.naturaleza === n).length }))
    .filter((x) => x.n > 0);
}

/** «$1.234.567» desde centavos, sin decimales. PURA. */
export function pesos(centavos) {
  const v = Math.round(centavos / 100);
  return `${v < 0 ? '−' : ''}$${Math.abs(v).toLocaleString('es-CO')}`;
}

/** «hace 9 min» · «hace 3 h» · «hace 2 días». `null` si no hay dato. PURA. */
export function haceCuanto(minutos) {
  if (minutos === null || minutos === undefined) return null;
  if (minutos < 60) return `hace ${minutos} min`;
  if (minutos < 48 * 60) return `hace ${Math.round(minutos / 60)} h`;
  return `hace ${Math.round(minutos / 1440)} días`;
}

/** La hora del corte en Colombia («26-oct 14:00»). PURA. */
export function horaCorte(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  const b = new Date(d.getTime() - 5 * 3600000);
  const mes = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][b.getUTCMonth()];
  return `${b.getUTCDate()}-${mes} ${String(b.getUTCHours()).padStart(2, '0')}:${String(b.getUTCMinutes()).padStart(2, '0')}`;
}
